<script lang="ts" setup>
  import { computed } from 'vue';
  import { CartLayout } from '@storefront/components';
  import { Features } from '@shared/features';
  import type { Dictionary, MedicalType, CustomerType, CountriesDictionary, RegionsDictionary } from '@othercommerce/domain';

  interface CustomerTypeDictionaryRecord {
    label: string;
    value: CustomerType;
  }

  interface MedicalTypeDictionaryRecord {
    label: string;
    value: MedicalType;
  }

  interface Props {
    hasMedicinalProducts?: boolean | undefined;
    customerTypes: Dictionary<CustomerTypeDictionaryRecord>;
    countries: CountriesDictionary;
    offer: boolean;
    registerAgencies?: Dictionary | undefined,
    medicalPracticeGroups?: Dictionary | undefined,
    medicalTypes?: Dictionary<MedicalTypeDictionaryRecord> | undefined,
    regions: RegionsDictionary,
  }

  defineOptions({
    layout: CartLayout,
    inheritAttrs: false,
  });

  const props = defineProps<Props>();

  const canGuestsOrder = computed(() => {
    return Features.ordersByGuests.isEnabled && !props.offer && !props.hasMedicinalProducts;
  });
</script>
<template>
  <main id="main" class="order order-account">
    <h1 class="order-account__header">
      {{ $t('SHOP.CART.HEADER.SIGN_IN_OR_GUEST') }}
    </h1>
    <div class="order-account__wrapper">
      <SfTile large>
        <h2 class="order-account__heading">
          {{ $t('SHOP.CART.HEADER.ALREADY_HAVE_AN_ACCOUNT') }}
        </h2>
        <AccountSignInForm />
        <div v-if="canGuestsOrder" class="order-account__separator">
          <span class="order-account__separator-span">
            {{ $t('SHOP.CART.MESSAGE.OR') }}
          </span>
        </div>
        <template v-if="canGuestsOrder">
          <h2 class=" order-account__heading">
            {{ $t('SHOP.CART.HEADER.CONTINUE_AS_GUEST') }}
          </h2>
          <SfButton :href="$route('web.cart.shipping.address.edit')" wide>
            {{ $t('SHOP.CART.ACTION.CHECKOUT_AS_GUEST') }}
          </SfButton>
        </template>
      </SfTile>
      <SfTile large>
        <template #title>
          <h2 class="order-account__heading">
            {{ $t('SHOP.CART.HEADER.CREATE_NEW_ACCOUNT') }}
          </h2>
        </template>
        <AccountSignUpForm :countries :customerTypes :medicalPracticeGroups :medicalTypes :regions :registerAgencies>
          <template #info>
            <AccountSignUpAdvantages />
          </template>
        </AccountSignUpForm>
      </SfTile>
    </div>
  </main>

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .order-account {
    max-width: 1400px;

    &__header {
      text-align: center;
    }

    &__wrapper {
      display: grid;
      grid-template-columns: 1fr 1fr;
      justify-content: center;
      align-items: start;
      gap: 4rem;
      margin: 40px auto 100px;

      .guest-text {
        font-size: 14px;
        margin-bottom: 25px;
      }
    }

    &__separator {
      position: relative;
      width: 100%;
      height: 1px;
      color: var(--color-secondary);
      text-align: center;
      margin: 1rem 0;
      background-color: var(--color-secondary);
    }

    &__separator-span {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      line-height: 1;
      font-size: 0.75rem;
      padding: 0 0.75rem;
      background: var(--sf-tile-background);
    }
  }

  @include media.query(lg) {
    .order-account {
      &__header {
        font-size: 1.5rem;
        margin-bottom: 1rem;
        padding: 0 0.625rem;
      }

      &__wrapper {
        grid-template-columns: 1fr;
        margin: 0;
        gap: 1rem;
      }

      &__heading {
        margin: 0 auto;
        font-size: 1.25rem;
      }
    }
  }
</style>

